(ns common.db.effects.sentry
  (:require ["@sentry/react" :as Sentry]
            [clojure.spec.alpha :as spec]
            [re-frame.core :as rf]))

(spec/def ::level (into #{} (comp (filter string?) (map keyword))
                        (vals (js->clj (.-Severity Sentry)))))

;; =================================== User ====================================
;; https://docs.sentry.io/platforms/javascript/guides/react/enriching-events/identify-user/


(spec/def ::user
  (spec/keys :req-un [(or :sentry.user/username :sentry.user/id :sentry.user/email)]))

(rf/reg-fx :sentry/user
  (fn [{{:keys [name id email]} :user :as session}]
    (let [user (-> session
                   (assoc :display-name name :id id :email email)
                   (dissoc :refreshToken :token :user))]
      (when (spec/valid? ::user user)
        (.setUser ^js Sentry (clj->js user))))))

;; ================================== Context ==================================
;; https://docs.sentry.io/platforms/javascript/guides/react/enriching-events/context/
;; Note the size limitations, don't use context to send e.g., the whole app
;; state.

(spec/def :sentry.context/name string?)
(spec/def :sentry.context/map map?)
(spec/def ::context (spec/tuple :sentry.context/name :sentry.context/map))

(rf/reg-fx :sentry/context
  (fn [[context-name context :as arg]]
    {:pre [(spec/valid? ::context arg)]}
    (.setContext ^js Sentry context-name (clj->js context))))

;; ==================================== Tag ====================================
;; https://docs.sentry.io/platforms/javascript/guides/react/enriching-events/tags/
;; The effect has exactly one parameter, which is a tuple (list, vector,
;; mapentry) consisting of the key and the value.

(spec/def :sentry.tag/key (spec/and string? #(<= (count %) 32)))
(spec/def :sentry.tag/value (spec/and string? #(<= (count %) 200)))
(spec/def ::tag (spec/tuple :sentry.tag/key :sentry.tag/value))

(rf/reg-fx :sentry/tag
  (fn [[k v :as tag]]
    {:pre [(spec/valid? ::tag tag)]}
    (.setTag ^js Sentry k v)))

;; ================================ Breadcrumb =================================
;; https://docs.sentry.io/platforms/javascript/guides/react/enriching-events/breadcrumbs/

;; Read
;; https://develop.sentry.dev/sdk/event-payloads/breadcrumbs/#breadcrumb-types
;; to check how to set 'data' for the associated breadcrumb type.
(spec/def :sentry.breadcrumb/type #{:default :debug :error :navigation :http :info :query :transaction :ui :user})

(spec/def :sentry.breadcrumb/timestamp (spec/or :rfc-3339 string?
                                                :seconds-since-epoch (spec/and number? pos?)))

(spec/def :sentry.breadcrumb/category string?) ; dotted string, e.g. 'ui.click'

(spec/def ::breadcrumb
  (spec/keys :req-un [(or :sentry.breadcrumb/type :sentry.breadcrumb/category
                          :sentry.breadcrumb/message ::level
                          :sentry.breadcrumb/timestamp :sentry.breadcrumb/data)]))

(rf/reg-fx :sentry/breadcrumb
  (fn [breadcrumb]
    {:pre [(spec/valid? ::breadcrumb breadcrumb)]}
    (.addBreadcrumb ^js Sentry (clj->js breadcrumb))))

;; ============================= Manual capturing ==============================
;; The effect has exactly one arg which can be:
;; - a string
;; - a js/Error object
;; - a sequential (list/vector/etc) with two elements:
;;   - first element is a string or js/Error object
;;   - second element is a map with some/all of the following keys:
;;     tags, extra, contexts, user, level, fingerprint

(spec/def :sentry.capture/exception (partial instance? js/Error))
(spec/def :sentry.capture/message string?)

(spec/def :sentry.capture/context
  (spec/keys :req-un [(or :sentry.capture/tags :sentry.capture/extra
                          :sentry.capture/contexts ::user
                          ::level :sentry.capture/fingerprint)]))

(spec/def ::capture
  (spec/or :exception
           (spec/or :exception :sentry.capture/exception
                    :with-optional-context (spec/cat :exception :sentry.capture/exception
                                                     :capture-context (spec/? :sentry.capture/context)))
           :message
           (spec/or
            :message :sentry.capture/message
            :with-optional-context (spec/cat :message :sentry.capture/message
                                             :capture-context (spec/? (spec/or :severity ::level
                                                                               :capture-context :sentry.capture/context))))))

(rf/reg-fx :sentry/capture
  (fn [arg]
    {:pre [(spec/valid? ::capture arg)]}
    (let [[capture-type [_ parsed-arg]] (spec/conform ::capture arg)
          msg-or-exc (get parsed-arg capture-type parsed-arg)
          capture-context (:capture-context parsed-arg)
          capture-context (clj->js (cond-> capture-context
                                     (vector? capture-context) second))]
      (case capture-type
        :message (.captureMessage ^js Sentry msg-or-exc capture-context)
        :exception (.captureException ^js Sentry msg-or-exc capture-context)))))
